import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../AppIcon";
import Button from "./Button";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentTheme, setCurrentTheme] = useState("default");
  const location = useLocation();

  const navigationItems = [
    { name: "Home", path: "/adaptive-homepage-hub", icon: "Home" },
    {
      name: "Portfolio",
      path: "/dynamic-portfolio-ecosystem",
      icon: "Briefcase",
    },
    { name: "Services", path: "/services-architecture-hub", icon: "Settings" },
    { name: "Case Studies", path: "/case-studies-showcase", icon: "FileText" },
    { name: "About", path: "/about", icon: "Info" },
    { name: "Contact", path: "/contact-scheduling-hub", icon: "Phone" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const detectThemeFromPath = () => {
      const path = location?.pathname;
      if (path?.includes("portfolio") || path?.includes("case-studies")) {
        setCurrentTheme("portfolio");
      } else if (path?.includes("services")) {
        setCurrentTheme("services");
      } else if (path?.includes("contact")) {
        setCurrentTheme("contact");
      } else {
        setCurrentTheme("default");
      }
    };

    detectThemeFromPath();
  }, [location?.pathname]);

  // Preload persistent HubSpot Meetings iframe once for faster subsequent opens
  useEffect(() => {
    if (document.getElementById("hs-meetings-iframe")) return;
    const host = document.createElement("div");
    host.id = "hs-meetings-iframe-host";
    host.style.position = "fixed";
    host.style.width = "0";
    host.style.height = "0";
    host.style.overflow = "hidden";
    host.style.opacity = "0";
    host.style.pointerEvents = "none";
    const iframe = document.createElement("iframe");
    iframe.id = "hs-meetings-iframe";
    iframe.src = "https://meetings-na2.hubspot.com/brian-wilkinson?embed=true";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.minWidth = "312px";
    iframe.style.minHeight = "615px";
    iframe.style.border = "none";
    iframe.style.overflow = "hidden";
    iframe.style.overscrollBehavior = "contain";
    iframe.style.display = "block";
    iframe.setAttribute("scrolling", "no");
    iframe.setAttribute("data-hs-ignore", "true");
    iframe.addEventListener(
      "load",
      () => {
        iframe.dataset.loaded = "true";
      },
      { once: true }
    );
    host.appendChild(iframe);
    document.body.appendChild(host);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/50 backdrop-blur-xl border-b border-border/50 shadow-lg`}
    >
      <div className="container mx-auto px-4 lg:px-0">
        <div className="flex items-center justify-between h-20">
          {/* Brand (text only) */}
          <Link
            to="/adaptive-homepage-hub"
            className="group flex items-center hover:opacity-90 transition-all duration-300"
            onClick={closeMenu}
          >
            <span
              className={`text-2xl font-brand tracking-wide transition-colors duration-300 text-primary`}
            >
              <span>Skycrest</span>
              <span className="text-accent">Films</span>
            </span>
          </Link>

          {/* Enhanced Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`group relative px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 flex items-center space-x-2 ${
                  isActivePath(item?.path)
                    ? "bg-accent/10 text-accent"
                    : "text-muted-foreground hover:text-accent hover:bg-accent/5"
                }`}
              >
                <Icon
                  name={item?.icon}
                  size={16}
                  className="group-hover:scale-110 transition-transform duration-300"
                />
                <span>{item?.name}</span>

                {/* Active Indicator */}
                {isActivePath(item?.path) && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-accent rounded-full"></div>
                )}
              </Link>
            ))}
            {/* Follow Us (Top Nav) */}
            <div className="ml-2 pl-2 border-l border-white/10 flex items-center space-x-2">
              <a
                href="https://instagram.com/skycrestfilms"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className={`text-muted-foreground hover:text-accent hover:bg-accent/5 w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-300`}
              >
                <Icon name="Instagram" size={16} />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                className={`text-muted-foreground hover:text-accent hover:bg-accent/5 w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-300`}
              >
                <Icon name="Youtube" size={16} />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className={`text-muted-foreground hover:text-accent hover:bg-accent/5 w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-300`}
              >
                <Icon name="Linkedin" size={16} />
              </a>
            </div>
          </nav>

          {/* Enhanced CTA Button & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <Button
              variant="default"
              size="sm"
              asChild
              className="hidden md:flex bg-cta hover:bg-cta/90 text-cta-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              iconName="Calendar"
              iconPosition="left"
            >
              <a
                href="#book-consultation"
                onClick={(e) => {
                  e.preventDefault();
                  // mount HubSpot Meetings widget (modal overlay)
                  if (!document.getElementById("MeetingsEmbedCode-js")) {
                    const s = document.createElement("script");
                    s.src =
                      "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
                    s.async = true;
                    s.defer = true;
                    s.id = "MeetingsEmbedCode-js";
                    document.body.appendChild(s);
                  }
                  let overlay = document.getElementById("hs-meetings-overlay");
                  if (!overlay) {
                    overlay = document.createElement("div");
                    overlay.id = "hs-meetings-overlay";
                    overlay.className =
                      "fixed inset-0 z-[60] flex justify-center items-start p-0 bg-[#425B76] md:bg-black/50";
                    overlay.style.overflow = "hidden";
                    overlay.style.overscrollBehavior = "contain";

                    const inner = document.createElement("div");
                    inner.className =
                      "relative w-screen h-screen md:w-full md:max-w-4xl md:h-[90vh] bg-[#425B76] md:bg-transparent rounded-none shadow-none p-0";
                    inner.style.overflow = "hidden";

                    // Loader placeholder
                    const loader = document.createElement("div");
                    loader.className =
                      "flex items-center justify-center h-full text-muted-foreground";
                    loader.innerHTML =
                      '<div class="animate-pulse">Loading…</div>';

                    // Obtain persistent iframe
                    let iframe = document.getElementById("hs-meetings-iframe");
                    if (!iframe) {
                      // Fallback: create if missing
                      iframe = document.createElement("iframe");
                      iframe.id = "hs-meetings-iframe";
                      iframe.src =
                        "https://meetings-na2.hubspot.com/brian-wilkinson?embed=true";
                      iframe.style.width = "100%";
                      iframe.style.height = "100%";
                      iframe.style.minWidth = "312px";
                      iframe.style.minHeight = "0";
                      iframe.style.border = "none";
                      iframe.style.overflow = "hidden";
                      iframe.style.overscrollBehavior = "contain";
                      iframe.style.display = "block";
                      iframe.setAttribute("scrolling", "no");
                      iframe.setAttribute("data-hs-ignore", "true");
                      iframe.addEventListener(
                        "load",
                        () => {
                          iframe.dataset.loaded = "true";
                          loader.style.display = "none";
                        },
                        { once: true }
                      );
                    }
                    // Mobile-only stretch to remove inner gutters without blurring
                    const isMobile =
                      window.matchMedia("(max-width: 767px)").matches;
                    if (isMobile) {
                      iframe.style.position = "absolute";
                      iframe.style.width = "110vw";
                      iframe.style.height = "110vh";
                      iframe.style.left = "-5vw";
                      iframe.style.top = "-5vh";
                    } else {
                      iframe.style.position = "static";
                      iframe.style.width = "100%";
                      iframe.style.height = "100%";
                      iframe.style.left = "0";
                      iframe.style.top = "0";
                    }

                    // Mount area for iframe
                    const mount = document.createElement("div");
                    mount.className = "h-full";
                    if (iframe.dataset.loaded === "true")
                      loader.style.display = "none";
                    mount.appendChild(loader);
                    mount.appendChild(iframe);
                    inner.appendChild(mount);

                    const close = document.createElement("button");
                    close.className =
                      "fixed top-4 right-4 md:absolute md:top-[16px] md:right-[-3px] z-[70] text-gray-500 hover:text-gray-900 bg-white hover:bg-white/80 rounded";
                    close.innerText = "×";
                    close.style.fontSize = "18px";
                    close.style.lineHeight = "1";
                    close.style.margin = "0";
                    close.style.padding = "2px 9px";
                    close.style.paddingBottom = "6px";
                    close.onclick = () => {
                      // Move iframe back to host to persist load
                      const host =
                        document.getElementById("hs-meetings-iframe-host") ||
                        (() => {
                          const h = document.createElement("div");
                          h.id = "hs-meetings-iframe-host";
                          h.style.position = "fixed";
                          h.style.width = "0";
                          h.style.height = "0";
                          h.style.overflow = "hidden";
                          h.style.opacity = "0";
                          h.style.pointerEvents = "none";
                          document.body.appendChild(h);
                          return h;
                        })();
                      if (iframe && host) host.appendChild(iframe);
                      document.body.style.overflow = "";
                      overlay.remove();
                    };
                    inner.appendChild(close);

                    overlay.appendChild(inner);
                    document.body.appendChild(overlay);
                    document.body.style.overflow = "hidden";
                  }
                }}
              >
                Book Consultation
              </a>
            </Button>

            {/* Enhanced Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className={`lg:hidden relative p-3 mt-1 rounded-xl transition-all duration-300 ${"text-muted-foreground hover:text-accent hover:bg-accent/5"}`}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-6">
                <span
                  className={`absolute block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? "rotate-45 translate-y-0" : "-translate-y-2"
                  }`}
                ></span>
                <span
                  className={`absolute block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? "opacity-0" : "opacity-100"
                  }`}
                ></span>
                <span
                  className={`absolute block w-6 h-0.5 bg-current transform transition-all duration-300 ${
                    isMenuOpen ? "-rotate-45 translate-y-0" : "translate-y-2"
                  }`}
                ></span>
              </div>
            </button>
          </div>
        </div>

        {/* Enhanced Mobile Navigation */}
        <div
          className={`lg:hidden transition-all duration-300 ${
            isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          } overflow-hidden`}
        >
          <div className="border-t border-border/20 bg-background/95 backdrop-blur-xl">
            <nav className="py-6 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-4 px-6 py-4 text-sm font-medium transition-all duration-300 ${
                    isActivePath(item?.path)
                      ? "bg-accent/10 text-accent border-l-4 border-accent"
                      : "text-muted-foreground hover:text-accent hover:bg-accent/5"
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.name}</span>
                </Link>
              ))}

              <div className="border-t border-border/20 my-4"></div>

              {/* Social links on mobile */}
              <div className="flex items-center space-x-3 px-6 py-2">
                <a
                  href="https://instagram.com/skycrestfilms"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="w-10 h-10 bg-accent/10 hover:bg-accent/20 rounded-xl flex items-center justify-center transition-colors duration-300"
                >
                  <Icon name="Instagram" size={18} className="text-accent" />
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="YouTube"
                  className="w-10 h-10 bg-accent/10 hover:bg-accent/20 rounded-xl flex items-center justify-center transition-colors duration-300"
                >
                  <Icon name="Youtube" size={18} className="text-accent" />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  className="w-10 h-10 bg-accent/10 hover:bg-accent/20 rounded-xl flex items-center justify-center transition-colors duration-300"
                >
                  <Icon name="Linkedin" size={18} className="text-accent" />
                </a>
              </div>

              <div className="px-6 pt-6">
                <Button
                  variant="default"
                  fullWidth
                  className="bg-cta hover:bg-cta/90 text-cta-foreground shadow-lg"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Book Consultation
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
